import { FC, PropsWithChildren, ReactNode, useMemo, useState } from "react";
import { OrganizationBillingTransferMode } from "@common/schemas/organization.schema";
import { StandardModal } from "@design-system/Modal/Modal";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useOverlays } from "contexts/OverlayContext";
import { EditClientModal } from "./EditClient/EditClientModal";
import { TransferBillingToClientModal } from "./TransferBillingToClient/TransferBillingToClientModal";
import { RequestBillingTransferToClientModal } from "./TransferBillingToClient/RequestBillingTransferToClientModal";
import { RequestBillingTransferFromClientModal } from "./RequestBillingTransferFromClientModal/RequestBillingTransferFromClientModal";
import { DeleteClientModal } from "./ClientRemoval/DeleteClient/DeleteClientModal";
import { TransferBillingAndRemoveClientModal } from "./ClientRemoval/TransferBillingAndRemoveClient/TransferBillingAndRemoveClientModal";
import { RemoveClientModal } from "./ClientRemoval/RemoveClient/RemoveClientModal";
import { ClientDetailsRow } from "./views/clientViewConfig";
import { ClientActionsContext } from "./clientActionsContext";

enum ClientAction {
    EDIT = "edit",
    TRANSFER_BILLING_TO_CLIENT = "transferBillingToClient",
    REQUEST_BILLING_TRANSFER_FROM_CLIENT = "requestBillingTransferFromClient",
    REMOVE_CLIENT = "removeClient",
    DELETE_CLIENT = "deleteClient",
    TRANSFER_BILLING_AND_REMOVE_CLIENT = "transferBillingAndRemoveClient",
}

interface EditClientState {
    type: ClientAction.EDIT;
    client: ClientDetailsRow;
}

interface TransferBillingToClientState {
    type: ClientAction.TRANSFER_BILLING_TO_CLIENT;
    client: ClientDetailsRow;
}

interface RequestBillingTransferFromClientState {
    type: ClientAction.REQUEST_BILLING_TRANSFER_FROM_CLIENT;
    client: ClientDetailsRow;
}

interface RemoveClientState {
    type: ClientAction.REMOVE_CLIENT;
    client: ClientDetailsRow;
}

interface TransferBillingAndRemoveClientState {
    type: ClientAction.TRANSFER_BILLING_AND_REMOVE_CLIENT;
    client: ClientDetailsRow;
}

interface DeleteClientState {
    type: ClientAction.DELETE_CLIENT;
    client: ClientDetailsRow;
}

type ClientActionState =
    | EditClientState
    | TransferBillingToClientState
    | RequestBillingTransferFromClientState
    | RemoveClientState
    | TransferBillingAndRemoveClientState
    | DeleteClientState;

export const ClientActionsProvider: FC<PropsWithChildren> = ({ children }) => {
    const [action, setAction] = useState<ClientActionState | null>(null);
    const { openOverlay } = useOverlays();
    const activeOrganization = useActiveOrganization();
    const isBillingTransferRequestMode =
        activeOrganization?.billingTransferMode ===
        OrganizationBillingTransferMode.REQUEST;

    const value = useMemo(
        () => ({
            addEntityToClient: (workspaceId: string) =>
                openOverlay("addEntity", { workspaceId }),
            editClient: (client: ClientDetailsRow) =>
                setAction({ type: ClientAction.EDIT, client }),
            transferBillingToClient: (client: ClientDetailsRow) =>
                setAction({
                    type: ClientAction.TRANSFER_BILLING_TO_CLIENT,
                    client,
                }),
            requestBillingTransferFromClient: (client: ClientDetailsRow) =>
                setAction({
                    type: ClientAction.REQUEST_BILLING_TRANSFER_FROM_CLIENT,
                    client,
                }),
            removeClient: (client: ClientDetailsRow) =>
                setAction({ type: ClientAction.REMOVE_CLIENT, client }),
            transferBillingAndRemoveClient: (client: ClientDetailsRow) =>
                setAction({
                    type: ClientAction.TRANSFER_BILLING_AND_REMOVE_CLIENT,
                    client,
                }),
            deleteClient: (client: ClientDetailsRow) =>
                setAction({ type: ClientAction.DELETE_CLIENT, client }),
        }),
        [openOverlay],
    );

    let modalContent: ReactNode;

    switch (action?.type) {
        case ClientAction.EDIT:
            modalContent = (
                <EditClientModal
                    client={action.client}
                    onClose={() => setAction(null)}
                />
            );
            break;
        case ClientAction.TRANSFER_BILLING_TO_CLIENT:
            modalContent = isBillingTransferRequestMode ? (
                <RequestBillingTransferToClientModal
                    client={action.client}
                    organizationId={activeOrganization.id}
                    onClose={() => setAction(null)}
                />
            ) : (
                <TransferBillingToClientModal
                    client={action.client}
                    onClose={() => setAction(null)}
                />
            );
            break;
        case ClientAction.REQUEST_BILLING_TRANSFER_FROM_CLIENT:
            modalContent = (
                <RequestBillingTransferFromClientModal
                    client={action.client}
                    onClose={() => setAction(null)}
                />
            );
            break;
        case ClientAction.REMOVE_CLIENT:
            modalContent = (
                <RemoveClientModal
                    client={action.client}
                    onClose={() => setAction(null)}
                />
            );
            break;
        case ClientAction.DELETE_CLIENT:
            modalContent = (
                <DeleteClientModal
                    client={action.client}
                    onClose={() => setAction(null)}
                />
            );
            break;
        case ClientAction.TRANSFER_BILLING_AND_REMOVE_CLIENT:
            modalContent = (
                <TransferBillingAndRemoveClientModal
                    client={action.client}
                    onClose={() => setAction(null)}
                />
            );
            break;
        default:
            modalContent = null;
            break;
    }

    return (
        <ClientActionsContext.Provider value={value}>
            {children}

            <StandardModal
                backdrop="static"
                show={!!action}
                onHide={() => setAction(null)}
            >
                {modalContent}
            </StandardModal>
        </ClientActionsContext.Provider>
    );
};
